import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, Route, Routes, useLocation } from 'react-router';
import { authClient } from './api';
import { AccountPage } from './pages/Account';
import { SupportPage, TicketPage } from './pages/Support';
import { StatusPage } from './pages/Status';
import { AdminPage } from './pages/Admin';
import { AuthPage, ResetPasswordPage } from './pages/Auth';
import { BillingPage } from './pages/Billing';
import { DatabaseDetailPage } from './pages/DatabaseDetail';
import { DatabaseDataPage } from './pages/DatabaseData';
import { DatabasesPage } from './pages/Databases';
import { OverviewPage } from './pages/Overview';
import { ProjectDetailPage } from './pages/ProjectDetail';
import { ProjectsPage } from './pages/Projects';
import { UsagePage } from './pages/Usage';
import { LandingPage } from './pages/Landing';
import { AgreementPage, LegalPage } from './pages/Legal';
import { AccountSwitcher, InvitePage } from './pages/Team';
import { useApi } from './api';
import { Logo, ThemeToggle } from './components';

function Layout() {
  const { data: session, isPending } = authClient.useSession();
  const location = useLocation();
  if (isPending) return <div className="main muted">Loading…</div>;
  if (!session) {
    if (location.pathname === '/') {
      // The marketing site lives at tallypg.com; the in-app landing is kept for local development only.
      if (import.meta.env.DEV) return <LandingPage />;
      window.location.replace('https://tallypg.com/');
      return null;
    }
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  const role = (session.user as { role?: string }).role;
  return <TermsGate role={role} />;
}

function LaunchNotice() {
  const [cfg, setCfg] = useState<{ launchNotice: string | null; paymentsEnabled: boolean } | null>(null);
  useEffect(() => {
    fetch('/api/public/config')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setCfg(d as { launchNotice: string | null; paymentsEnabled: boolean } | null))
      .catch(() => setCfg(null));
  }, []);
  if (!cfg?.launchNotice) return null;
  return <div className="banner">{cfg.launchNotice}</div>;
}

function TermsGate({ role }: { role?: string }) {
  const location = useLocation();
  const terms = useApi<{ version: string; accepted: boolean }>('/account/terms');
  if (terms.loading && !terms.data) return <div className="main muted">Loading…</div>;
  if (terms.data && !terms.data.accepted && location.pathname !== '/agree')
    return <Navigate to="/agree" replace />;
  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">
          <Logo />
          <span>
            TallyPG
            <small>Normal PostgreSQL. Always available.</small>
          </span>
        </div>
        <AccountSwitcher />
        <NavLink to="/" end>
          Overview
        </NavLink>
        <NavLink to="/projects">Projects</NavLink>
        <NavLink to="/databases">Databases</NavLink>
        <NavLink to="/usage">Usage</NavLink>
        <NavLink to="/billing">Billing</NavLink>
        <NavLink to="/account">Account</NavLink>
        <NavLink to="/support">Support</NavLink>
        {role === 'admin' && <NavLink to="/admin">Admin</NavLink>}
        <div className="spacer" />
        <a href="/api/v1/openapi.json" target="_blank" rel="noreferrer" className="muted">
          API reference
        </a>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            void authClient.signOut().then(() => window.location.assign('/login'));
          }}
        >
          Sign out
        </a>
      </aside>
      <main className="main">
        <LaunchNotice />
        <Outlet />
      </main>
    </div>
  );
}

export function App() {
  // status.tallypg.com serves only the status page, whatever the path.
  if (typeof window !== 'undefined' && window.location.hostname.startsWith('status.')) return <StatusPage />;
  return (
    <>
      <ThemeToggle />
      <Routes>
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/signup" element={<AuthPage mode="signup" />} />
        <Route path="/forgot-password" element={<AuthPage mode="forgot" />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/terms" element={<LegalPage kind="terms" />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/status" element={<StatusPage />} />
        <Route element={<Layout />}>
          <Route index element={<OverviewPage />} />
          <Route path="/agree" element={<AgreementPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="/databases" element={<DatabasesPage />} />
          <Route path="/databases/:databaseId" element={<DatabaseDetailPage />} />
          <Route path="/databases/:databaseId/data" element={<DatabaseDataPage />} />
          <Route path="/usage" element={<UsagePage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/support/:ticketId" element={<TicketPage />} />
          <Route path="/invite/:token" element={<InvitePage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
